/**
 * Shared layout width for the docked Tower Assistant aside (#673).
 *
 * Lives in its own dependency-free module so both the lazy-loaded
 * {@link ./AgentDrawer} and the app shell (`router.tsx`) can import the width
 * without pulling the heavy drawer chunk into the entry bundle.
 */

/** Default width when nothing is stored yet. */
export const AGENT_DRAWER_WIDTH = 500;

/** Smallest width the user can drag to. */
export const AGENT_DRAWER_MIN_WIDTH = 320;

/** Largest width the user can drag to. */
export const AGENT_DRAWER_MAX_WIDTH = 800;

const STORAGE_KEY = "tower.agentDrawer.width";

/** Reads a persisted drawer width, falling back to the default. */
export function readAgentDrawerWidth(): number {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return AGENT_DRAWER_WIDTH;
    }
    const parsed = Number.parseInt(raw, 10);
    return clampAgentDrawerWidth(parsed);
  } catch {
    return AGENT_DRAWER_WIDTH;
  }
}

/** Persists the drawer width for the next session. */
export function writeAgentDrawerWidth(width: number): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, String(clampAgentDrawerWidth(width)));
  } catch {
    // Private mode / quota — width still works for this session.
  }
}

/** Clamps a width into the allowed resize range. */
export function clampAgentDrawerWidth(width: number): number {
  if (!Number.isFinite(width)) {
    return AGENT_DRAWER_WIDTH;
  }
  return Math.min(AGENT_DRAWER_MAX_WIDTH, Math.max(AGENT_DRAWER_MIN_WIDTH, Math.round(width)));
}
